// src/pages/Admin/AdminProductForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsAPI } from '../../api/products.js';
import { categoriesAPI } from '../../api/categories.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';

export const AdminProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const isEdit = id && id !== 'new';
  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    sku: '',
    price: '',
    old_price: '',
    price_source: 'fixed',
    short_description: '',
    full_description: '',
    category_ids: [],
    details: [],
    images: [],
    variants: [],
    is_new: false,
    is_active: true,
    stock_quantity: 0,
    stock_status: 'on_request',
    weight: '',
    width: '',
    height: '',
    depth: '',
    unit: 'шт',
    meta_title: '',
    meta_description: '',
  });

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      // ⚠️ Запрос списка калькуляторов убран 2026-10-10: таблицы
      //    `calculators` больше нет, источник цены — поле price_source.
      const categoriesRes = await categoriesAPI.getAll({ is_active: true });
      setCategories(categoriesRes.data.data || []);

      if (isEdit) {
        const productRes = await productsAPI.getById(id);
        const product = productRes.data.data;
        setFormData({
          ...product,
          price: product.price || '',
          old_price: product.old_price || '',
          category_ids: product.Categories?.map(c => c.id) || [],
          details: product.ProductDetails || [],
          images: product.ProductImages || [],
          variants: product.ProductVariants || [],
        });
      }
    } catch (err) {
      error('Не удалось загрузить данные');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.slug || !formData.sku) {
      error('Заполните обязательные поля: Название, Slug и Артикул');
      return;
    }

    setIsLoading(true);
    try {
      const submitData = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
        old_price: formData.old_price ? parseFloat(formData.old_price) : null,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        depth: formData.depth ? parseFloat(formData.depth) : null,
        stock_quantity: formData.stock_quantity ? parseInt(formData.stock_quantity) : 0,
      };

      if (isEdit) {
        await productsAPI.update(id, submitData);
        success('Товар обновлен');
      } else {
        await productsAPI.create(submitData);
        success('Товар создан');
      }
      navigate('/admin/products');
    } catch (err) {
      error(err.response?.data?.message || 'Не удалось сохранить товар');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddDetail = () => {
    setFormData({
      ...formData,
      details: [...formData.details, { key: '', value: '', sort_order: formData.details.length }],
    });
  };

  const handleRemoveDetail = (index) => {
    setFormData({
      ...formData,
      details: formData.details.filter((_, i) => i !== index),
    });
  };

  const handleDetailChange = (index, field, value) => {
    const newDetails = [...formData.details];
    newDetails[index][field] = value;
    setFormData({ ...formData, details: newDetails });
  };

  const handleAddImage = () => {
    setFormData({
      ...formData,
      images: [...formData.images, { url: '', alt_text: '', sort_order: formData.images.length }],
    });
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  const handleImageChange = (index, field, value) => {
    const newImages = [...formData.images];
    newImages[index][field] = value;
    setFormData({ ...formData, images: newImages });
  };

  if (isLoading && isEdit) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;
  }

  return (
    <div style={{
      backgroundColor: 'rgb(var(--c-surface))',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <form onSubmit={handleSubmit}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>
          {isEdit ? 'Редактировать товар' : 'Новый товар'}
        </h2>

        {/* Основная информация */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="Название *"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Slug *"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
          <Input
            label="Артикул *"
            value={formData.sku}
            onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
            required
          />
          <Input
            label="Цена"
            type="number"
            value={formData.price}
            onChange={(e) => setFormData({ ...formData, price: e.target.value })}
          />
          <Input
            label="Старая цена (скидка)"
            type="number"
            value={formData.old_price}
            onChange={(e) => setFormData({ ...formData, old_price: e.target.value })}
          />
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Категории
            </label>
            <select
              multiple
              value={formData.category_ids}
              onChange={(e) => {
                const values = Array.from(e.target.selectedOptions, option => option.value);
                setFormData({ ...formData, category_ids: values });
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
                minHeight: '80px',
              }}
            >
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
            <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px' }}>
              Hold Ctrl (Cmd) для выбора нескольких
            </div>
          </div>
        </div>

        <div style={{ height: '16px' }} />

        {/* Описания */}
        <Input
          label="Краткое описание"
          value={formData.short_description}
          onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
          placeholder="Для карточек товара"
        />
        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Полное описание
          </label>
          <textarea
            value={formData.full_description}
            onChange={(e) => setFormData({ ...formData, full_description: e.target.value })}
            placeholder="Для страницы товара"
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '120px',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ height: '16px' }} />

        {/* Характеристики */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Характеристики</h3>
            <Button type="button" size="sm" onClick={handleAddDetail}>
              + Добавить
            </Button>
          </div>
          {formData.details.map((detail, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '1fr 2fr auto',
              gap: '8px',
              marginBottom: '8px',
              alignItems: 'center',
            }}>
              <Input
                value={detail.key}
                onChange={(e) => handleDetailChange(index, 'key', e.target.value)}
                placeholder="Название"
              />
              <Input
                value={detail.value}
                onChange={(e) => handleDetailChange(index, 'value', e.target.value)}
                placeholder="Значение"
              />
              <button
                type="button"
                onClick={() => handleRemoveDetail(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgb(var(--c-danger))',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ height: '16px' }} />

        {/* Изображения */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600 }}>Изображения</h3>
            <Button type="button" size="sm" onClick={handleAddImage}>
              + Добавить
            </Button>
          </div>
          {formData.images.map((image, index) => (
            <div key={index} style={{
              display: 'grid',
              gridTemplateColumns: '2fr 1fr auto',
              gap: '8px',
              marginBottom: '8px',
              alignItems: 'center',
            }}>
              <Input
                value={image.url}
                onChange={(e) => handleImageChange(index, 'url', e.target.value)}
                placeholder="URL изображения"
              />
              <Input
                value={image.alt_text}
                onChange={(e) => handleImageChange(index, 'alt_text', e.target.value)}
                placeholder="Alt текст"
              />
              <button
                type="button"
                onClick={() => handleRemoveImage(index)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'rgb(var(--c-danger))',
                  cursor: 'pointer',
                  fontSize: '18px',
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>

        <div style={{ height: '16px' }} />

        {/* Настройки */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Источник цены
            </label>
            {/* 🔑 «Зашитый» механизм NORRAN (решение владельца 2026-10-10).
                Сайт — самостоятельный магазин: по умолчанию цена фиксированная
                и берётся из поля «Цена». Режим NORRAN оставлен на будущее —
                он включится, когда в NORRAN заполнят шаги калькуляторов.
                Значение уходит в `products.price_source` (БД его валидирует
                CHECK-ограничением). */}
            <select
              value={formData.price_source || 'fixed'}
              onChange={(e) => setFormData({ ...formData, price_source: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="fixed">Фиксированная (цена из Студии)</option>
              <option value="norran" disabled>
                Расчёт NORRAN — пока недоступен
              </option>
            </select>
            <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-muted))', marginTop: '4px' }}>
              {(formData.price_source || 'fixed') === 'fixed'
                ? 'Цена берётся из поля «Цена» ниже.'
                : 'Цена будет рассчитана в NORRAN по коду калькулятора.'}
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Статус наличия
            </label>
            <select
              value={formData.stock_status}
              onChange={(e) => setFormData({ ...formData, stock_status: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="in_stock">В наличии</option>
              <option value="out_of_stock">Нет в наличии</option>
              <option value="pre_order">Предзаказ</option>
              <option value="on_request">Под заказ</option>
            </select>
          </div>
          <Input
            label="Количество на складе"
            type="number"
            value={formData.stock_quantity}
            onChange={(e) => setFormData({ ...formData, stock_quantity: e.target.value })}
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_new}
                onChange={(e) => setFormData({ ...formData, is_new: e.target.checked })}
              />
              Новинка
            </label>
          </div>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Активен
            </label>
          </div>
        </div>

        <div style={{ height: '24px' }} />

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button type="submit" isLoading={isLoading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/products')}>
            Отмена
          </Button>
        </div>
      </form>
    </div>
  );
};